/** Popover showing species photo and info link.

A small (i) icon that opens a popover with the species image and a link
to the full LUBW steckbrief PDF. Self-contained: manages its own
open/close state and outside-click dismissal.

The popover is rendered in a portal to document.body (not as a normal
absolutely-positioned child) because the sidebar it lives in has
`overflow-y-auto`, which — per the CSS spec — also clips overflow-x, so a
popover wider than the sidebar would be clipped away. A portal escapes that
clipping container; position is computed from the trigger button's rect.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useLanguageContext } from "../hooks/useLanguage";
import type { SpeciesConfig } from "../types";

interface SpeciesInfoPopoverProps {
  species: SpeciesConfig;
}

const POPOVER_WIDTH = 320; // matches w-80
const VIEWPORT_PAD = 8;
const GAP = 6;

export function SpeciesInfoPopover({ species }: SpeciesInfoPopoverProps) {
  const { t } = useLanguageContext();
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number; width: number; maxHeight: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  /** Get the localised species name, falling back to the config name. */
  const localisedName = (): string => {
    const key = `species_${species.id}`;
    const val = t(key);
    return val === key ? species.name : val;
  };

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(POPOVER_WIDTH, Math.max(220, window.innerWidth - VIEWPORT_PAD * 2));
    const left = Math.max(
      VIEWPORT_PAD,
      Math.min(rect.left, window.innerWidth - width - VIEWPORT_PAD),
    );
    const belowTop = rect.bottom + GAP;
    const spaceBelow = window.innerHeight - belowTop - VIEWPORT_PAD;
    const spaceAbove = rect.top - GAP - VIEWPORT_PAD;
    const openAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
    const available = Math.max(120, openAbove ? spaceAbove : spaceBelow);
    const top = openAbove
      ? Math.max(VIEWPORT_PAD, rect.top - GAP - available)
      : Math.min(belowTop, window.innerHeight - VIEWPORT_PAD - available);
    setCoords({ left, top: Math.max(VIEWPORT_PAD, top), width, maxHeight: available });
  }, []);

  // Position before paint so it never flashes in the wrong place.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // Outside-click / Escape dismissal, plus keep the popover anchored to the
  // trigger while the sidebar scrolls or the window resizes.
  useEffect(() => {
    if (!open) return;
    const handlePointerOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true); // capture: catch sidebar scroll
    return () => {
      document.removeEventListener("pointerdown", handlePointerOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, reposition]);

  // Don't render the icon if there's no image and no info URL
  if (!species.image && !species.info_url) return null;

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen((prev) => !prev);
        }}
        aria-expanded={open}
        aria-controls={popoverId}
        aria-label={`${t("species_label")}: ${localisedName()}`}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-flima-spruce/20 bg-white text-flima-slate/65 transition-colors hover:border-flima-river hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none"
      >
        <svg
          viewBox="0 0 20 20"
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="7.5" />
          <path d="M10 9.5v4M10 6.5h.01" strokeLinecap="round" />
        </svg>
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={popoverRef}
              id={popoverId}
              role="dialog"
              aria-label={species.name}
              style={{
                left: coords.left,
                top: coords.top,
                width: coords.width,
                maxHeight: coords.maxHeight,
              }}
              className="fixed z-[9999] overflow-y-auto rounded-lg border border-flima-spruce/10 bg-white p-3 shadow-panel"
            >
              {species.image && !imgError ? (
                <img
                  src={species.image}
                  alt={localisedName()}
                  loading="lazy"
                  onError={() => setImgError(true)}
                  className="mb-2 h-40 w-full rounded-md bg-flima-mist/40 object-contain"
                />
              ) : null}
              <p className="font-display text-sm font-semibold text-flima-spruce">
                {localisedName()}
              </p>
              <p className="mt-0.5 text-xs italic text-flima-slate/60">{species.latin}</p>
              {species.info_url ? (
                <a
                  href={species.info_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block break-words text-xs font-semibold text-flima-river underline decoration-flima-river/30 underline-offset-2 hover:text-flima-spruce focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river"
                >
                  {t("species_info_link")}
                </a>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
